import { useT } from '@/store';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { formatKm } from '@/lib/format';

/**
 * When each truck is next due in the workshop — and, beside it, how often
 * this carrier has actually made those dates.
 *
 * AUDIT.md section 4 listed "share of services done on time" as NOT MEASURED
 * with the shortest note in the table: there was no service module at all.
 * core/downtime.py counts the hours a truck spent off the road AFTER they
 * were lost; core/servicing.py is the half that comes before them, and this
 * panel is the only place a carrier ever reads it.
 *
 * IT RENDERS THE ENGINE'S REFUSALS, IT DOES NOT SMOOTH THEM OVER. Every
 * honesty rule in core/servicing.py's docstring is a rendering rule here,
 * because a rule enforced in Python and undone in JSX was never enforced:
 *
 *  - `km_status: "unknown"` is a DATA GAP, not a verdict. It is drawn in
 *    accent, never in green (which would read as "fine") and never in red
 *    (which would read as "this truck is broken"). Nothing is wrong with the
 *    truck; something is missing from the screen the carrier types into, and
 *    the row says which screen.
 *
 *  - `km_remaining` is null on every unknown row, deliberately: the engine
 *    refuses to print "-3 000 km" beside the word "unknown" because the
 *    reader takes the number and leaves the word. Null renders as an
 *    em-dash, the codebase convention — never 0, which is a measurement
 *    meaning "due exactly now".
 *
 *  - The four `km_reason` values get four DIFFERENT sentences. Each names a
 *    different five-second fix — type the last service's odometer in, link
 *    the fuel card, the reading is too old, the reading has no date — and
 *    collapsing them into one "odometer problem" sends a carrier to the
 *    wrong screen, where he finds nothing wrong and stops trusting the row.
 *
 *  - `odometer_source` travels with the reading. A number off a fuel-card
 *    statement is dated by the statement; a number somebody typed into the
 *    vehicle form is dated by whenever they last touched it. An assumption
 *    and a measurement must not look identical on screen, so they are two
 *    different sentences rather than two shades of grey.
 *
 *  - `vehicles_without_plan` is drawn whenever it is non-zero, in amber,
 *    above the table. A truck with no plan has not passed its service; it
 *    has never been given one, and it is in none of the counts. An all-green
 *    screen that hides those trucks is the exact lie this module exists to
 *    stop telling.
 *
 * THE ON-TIME SHARE IS NULL WHEN NOTHING COULD BE JUDGED, AND NULL IS NOT
 * ZERO. "0% on time" and "no evidence either way" are opposite messages to
 * an owner — one is a fleet in trouble, the other is a fleet with no
 * history typed in yet — and the engine returns None rather than 0.0 for
 * exactly that reason. This panel prints an em-dash and the sentence saying
 * why, and prints a real 0,0% only when there genuinely were pairs to judge
 * and every one of them was late.
 *
 * The share is rendered to one decimal with a Lithuanian decimal comma, NOT
 * through formatCompactNumber(): that helper gives one decimal below 1000,
 * so 0.35 and 0.43 both come out "0.4" — see the comment on FuelPanel's
 * perLitre(), which is the bug this convention was written after. Two
 * quarters that differ by four points of compliance must not print the same.
 *
 * Read-only, like every panel in this family: the plans and the records are
 * written through blueprints/servicing.py's CRUD, never from here.
 */

/** The codebase's "no number at all". Never a blank cell (which reads as a
 *  rendering failure) and never a zero (which reads as a measurement). */
const EM_DASH = '—';

/**
 * The key core/servicing.UNKNOWN_KM_NOTE_KEY is expected to send, written
 * out as a literal on purpose — the same device FuelPanel and DowntimePanel
 * use for the same reason. tools/check_i18n.py reads source TEXT, so a key
 * only ever reached through a variable is invisible to it, and a key dropped
 * from the bundle would reach a carrier as the raw string
 * "servicing.unknownKmBasis" where the sentence explaining that "unknown" is
 * not "ok" should be.
 */
const UNKNOWN_KM_NOTE_KEY = 'servicing.unknownKmBasis';

/** Worst first — the same order core/servicing._STATUS_ORDER sorts the rows
 *  in, so the chips above the table read in the same direction as the table.
 *  `unknown` sits above `ok`: a row nobody can answer for needs a human more
 *  than a row that is fine. */
const STATUS_ORDER = ['overdue', 'due_soon', 'no_baseline', 'unknown', 'ok'];

/** Literal keys, one per status, rather than `servicing.status.${status}`.
 *  Same reason as UNKNOWN_KM_NOTE_KEY above. */
const STATUS_KEYS = {
  overdue: 'servicing.status.overdue',
  due_soon: 'servicing.status.due_soon',
  no_baseline: 'servicing.status.no_baseline',
  unknown: 'servicing.status.unknown',
  ok: 'servicing.status.ok',
};

/**
 * Colour carries the same distinction the words do. The two data-gap states
 * are structure and accent — informational, actionable — and neither is
 * red: a carrier who sees red goes and looks at the truck, and there is
 * nothing wrong with the truck. Neither is green either, which would say the
 * deadline had been met by a row where nobody knows.
 */
const STATUS_TONE = {
  overdue: 'bg-red/15 text-red',
  due_soon: 'bg-amber/15 text-amber',
  no_baseline: 'bg-structure/15 text-structure',
  unknown: 'bg-accent/15 text-accent',
  ok: 'bg-green/15 text-green',
};

/**
 * One sentence per reason, because each one names a DIFFERENT thing to go
 * and do. Written as four literals so tools/check_i18n.py can see all four,
 * and so that deleting one is a visible deletion rather than a fallback that
 * quietly starts printing another reason's fix.
 */
const KM_REASON_KEYS = {
  last_service_has_no_odometer: 'servicing.kmReason.last_service_has_no_odometer',
  no_current_odometer: 'servicing.kmReason.no_current_odometer',
  odometer_reading_stale: 'servicing.kmReason.odometer_reading_stale',
  undated_odometer: 'servicing.kmReason.undated_odometer',
};

/**
 * The one reason that is NOT about the odometer, and therefore not in the
 * map above and not in the kilometre column.
 *
 * A months-only plan whose last service carries a date nothing can parse
 * produces no deadline on either half, so the engine reports the row as
 * unknown rather than letting it sit green forever. Its km_status is null,
 * which the kilometre column correctly renders as an em-dash — that column
 * means "this rule sets no kilometre interval", and nothing is missing
 * there. The broken thing is the DATE, so the sentence belongs beside the
 * date, next to the other call to action that lives there.
 */
const DATE_REASON_KEYS = {
  last_service_date_unreadable: 'servicing.kmReason.last_service_date_unreadable',
};

/** Where today's reading came from. A dated statement line and a number
 *  somebody typed into a form are not the same claim. */
const ODOMETER_SOURCE_KEYS = {
  fuel_fill: 'servicing.odometer.fuel_fill',
  manual_entry: 'servicing.odometer.manual_entry',
};

/** The three ways a performed service can fail to be judgeable. Counted and
 *  shown rather than dropped — a shrinking denominator would make a carrier
 *  with almost no history look almost perfect. */
const UNJUDGEABLE_KEYS = {
  no_predecessor: 'servicing.unjudgeable.no_predecessor',
  no_plan: 'servicing.unjudgeable.no_plan',
  no_common_basis: 'servicing.unjudgeable.no_common_basis',
};

/**
 * Kilometres, grouped, exact.
 *
 * NOT formatCompactNumber(): it rounds 12 000 and 12 400 both to "12k", and
 * a 400 km difference is the difference between booking the workshop this
 * week and next. The same helper that made 0.35 and 0.43 print identically
 * on the fuel panel would make two different service thresholds print
 * identically here.
 */
function km(value) {
  // One implementation, in @/lib/format, shared with the vehicle card above
  // this panel: the two rendered the same odometer in two different formats
  // on one screen until they were joined up.
  return formatKm(value);
}

/**
 * The on-time share as a percentage, to one decimal, with a Lithuanian
 * decimal comma.
 *
 * One decimal and not zero: a quarter at 82,4% and one at 87,5% are a
 * different conversation with a workshop, and rounded to whole percent they
 * are close enough to look like noise. Null returns the em-dash — the caller
 * also prints the sentence, because an em-dash alone tells a carrier nothing
 * about what to do next.
 */
function sharePct(value) {
  if (value == null) return EM_DASH;
  return `${(value * 100).toFixed(1).replace('.', ',')}%`;
}

/**
 * The calendar half of one row.
 *
 * `due_date` is null when the plan has no month interval at all, and also
 * when there is no last service to count from — both are "no date deadline
 * here", and neither is a date in the past.
 */
function dateDeadline(t, row) {
  if (row.due_date == null) return null;
  if (row.days_until == null) return row.due_date;
  if (row.days_until < 0) return t('servicing.overdueBy', { n: Math.abs(row.days_until) });
  if (row.days_until === 0) return t('servicing.dueToday');
  return t('servicing.dueIn', { n: row.days_until });
}

/**
 * The kilometre half of one row.
 *
 * Guarded on `km_remaining` rather than on `km_status`, because the engine's
 * promise is about the NUMBER: it is null on every row where printing a
 * figure would be read instead of the word beside it. A null here is an
 * em-dash and never a zero — "0 km left" is a measurement saying the truck
 * is due this minute, which is the opposite of "nobody knows".
 */
function kmDeadline(t, row) {
  if (row.km_remaining == null) return EM_DASH;
  if (row.km_status === 'overdue') return t('servicing.kmOverdueBy', { n: km(row.km_remaining) });
  return t('servicing.kmRemaining', { n: km(row.km_remaining) });
}

/**
 * Where today's odometer came from, when it was taken, and how old it is.
 *
 * A reading with no date is shown WITH ITS VALUE: the carrier typed that
 * number and needs to see it, or the row reads as "the system lost my
 * odometer" rather than as "nothing says when this was measured".
 */
function odometerProvenance(t, row) {
  if (row.current_odometer_km == null) return t('servicing.odometerNone');
  const source = ODOMETER_SOURCE_KEYS[row.odometer_source];
  const label = source ? t(source) : t('servicing.odometerUnknownSource');
  if (row.odometer_as_of == null) {
    return t('servicing.odometerUndated', { s: label, n: km(row.current_odometer_km) });
  }
  return t('servicing.odometerAsOf', {
    s: label,
    n: km(row.current_odometer_km),
    d: row.odometer_as_of,
    a: row.odometer_age_days ?? 0,
  });
}

/**
 * What is due, and how often this carrier has made the date.
 *
 * Props (both read-only; the parent owns the queries):
 *   due     — the GET /api/tenants/<id>/service/due body, i.e. core
 *             servicing.due_list(): { rows, capped, total_rows, counts,
 *             vehicles_checked, vehicles_without_plan, generated_at }.
 *   onTime  — the GET /api/tenants/<id>/service/on-time body, i.e. core
 *             servicing.on_time_share(): { share, judged_n, on_time_n,
 *             late_n, late_by, unjudgeable_n, unjudgeable }.
 *   isLoading / isError / error — the combined query state, in the shape
 *             react-query hands back.
 *
 * Either data prop may be undefined (its query has not resolved, or failed);
 * the half that is missing is simply not drawn. It is never stood in for
 * with zeroes.
 */
export function ServicePanel({ due, onTime, isLoading = false, isError = false, error = null }) {
  const t = useT();
  const rows = due?.rows ?? [];
  const counts = due?.counts ?? {};

  return (
    <Panel title={t('servicing.title')}>
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : (
        <>
          <p className="mb-3 text-[11px] leading-snug text-dim">{t('servicing.intro')}</p>

          {onTime ? <OnTimeShare data={onTime} /> : null}

          {due ? (
            <>
              <div className="mb-2 flex flex-wrap gap-1">
                {STATUS_ORDER.map((status) => (
                  <span
                    key={status}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.03em] ${STATUS_TONE[status]}`}
                  >
                    {t(STATUS_KEYS[status])} {counts[status] ?? 0}
                  </span>
                ))}
              </div>

              {/* Trucks the table cannot possibly contain, said before the
                  table rather than under it. They are in none of the counts
                  above, they have no deadline at all, and a carrier reading
                  a green screen without this line is reading a fleet that
                  is smaller than his own. */}
              {due.vehicles_without_plan > 0 ? (
                <p className="mb-2 text-[10.5px] leading-snug text-amber">
                  {t('servicing.withoutPlan', { n: due.vehicles_without_plan })}
                </p>
              ) : null}

              {/* Said only when there is at least one such row, and said in
                  full: "unknown" is the one word on this panel a reader is
                  most likely to translate into "probably fine". */}
              {(counts.unknown ?? 0) > 0 ? (
                <p className="mb-2 text-[10.5px] leading-snug text-accent">
                  {t(UNKNOWN_KM_NOTE_KEY)}
                </p>
              ) : null}

              {rows.length === 0 ? (
                <EmptyState>{t('servicing.none')}</EmptyState>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11.5px]">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.03em] text-dim">
                        <th className="pb-1.5 pr-3 font-bold">{t('drivers.plateLabel')}</th>
                        <th className="pb-1.5 pr-3 font-bold">{t('servicing.colService')}</th>
                        <th className="pb-1.5 pr-3 font-bold">{t('servicing.colStatus')}</th>
                        <th className="pb-1.5 pr-3 font-bold">{t('servicing.colDate')}</th>
                        <th className="pb-1.5 font-bold">{t('servicing.colKm')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <ServiceRow key={`${row.vehicle_id}:${row.plan_id}`} row={row} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-2 flex flex-wrap gap-x-3 text-[10.5px] text-dim">
                {/* The counts above cover the whole fleet; the table does
                    not. A carrier with ninety trucks must not read sixty
                    rows as all of them. */}
                {due.capped ? (
                  <span>{t('servicing.capped', { n: rows.length, m: due.total_rows })}</span>
                ) : null}
                <span>{t('servicing.vehiclesChecked', { n: due.vehicles_checked ?? 0 })}</span>
              </div>
            </>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/**
 * The share, and beside it what could not be judged at all.
 *
 * The two boxes are the same size on purpose. The second one is not a
 * footnote to the first: a carrier whose history is three months old has
 * almost everything in the right-hand box, and the percentage on the left is
 * standing on four pairs of records.
 */
function OnTimeShare({ data }) {
  const t = useT();
  const unjudgeable = data.unjudgeable ?? {};
  const lateBy = data.late_by ?? {};

  return (
    <div className="mb-3 grid gap-2 sm:grid-cols-2">
      <div className="rounded-[11px] border border-line bg-white/2 p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.03em] text-dim">
          {t('servicing.onTimeTitle')}
        </div>
        {/* null is not zero, and this is the single most important line in
            the panel. "0% on time" is a fleet in trouble; "nothing could be
            judged" is a fleet whose history has not been typed in. An owner
            acts differently on each, so they must not render alike. */}
        {data.share == null ? (
          <>
            <div className="text-[18px] font-bold text-dim">{EM_DASH}</div>
            <div className="text-[11.5px] leading-snug text-amber">{t('servicing.onTimeNone')}</div>
          </>
        ) : (
          <>
            <div className="text-[18px] font-bold tabular-nums">{sharePct(data.share)}</div>
            <div className="mt-0.5 text-[10.5px] text-dim">
              {t('servicing.onTimeJudged', {
                n: data.judged_n,
                m: data.on_time_n,
                l: data.late_n,
              })}
            </div>
            {/* Which deadline was missed. "Every 60 000 km or 12 months,
                whichever comes first" is one rule and a pair is late if it
                missed either half; a carrier fixing this needs to know which
                half he is missing. */}
            {data.late_n > 0 ? (
              <div className="text-[10.5px] text-amber">
                {t('servicing.lateByDate', { n: lateBy.late_by_date ?? 0 })}
                {' · '}
                {t('servicing.lateByKm', { n: lateBy.late_by_km ?? 0 })}
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="rounded-[11px] border border-line bg-white/2 p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.03em] text-dim">
          {t('servicing.unjudgeableTitle')}
        </div>
        <div className="text-[18px] font-bold tabular-nums">{data.unjudgeable_n ?? 0}</div>
        <div className="mt-0.5 text-[10.5px] text-dim">
          {t(UNJUDGEABLE_KEYS.no_predecessor, { n: unjudgeable.no_predecessor ?? 0 })}
          {' · '}
          {t(UNJUDGEABLE_KEYS.no_plan, { n: unjudgeable.no_plan ?? 0 })}
          {' · '}
          {t(UNJUDGEABLE_KEYS.no_common_basis, { n: unjudgeable.no_common_basis ?? 0 })}
        </div>
        <div className="mt-0.5 text-[10.5px] leading-snug text-dim">
          {t('servicing.unjudgeableNote')}
        </div>
      </div>
    </div>
  );
}

/** One (vehicle × plan) deadline. */
function ServiceRow({ row }) {
  const t = useT();
  const dateText = dateDeadline(t, row);

  return (
    <tr className="border-t border-line align-top">
      <td className="py-1.5 pr-3 font-semibold">
        {row.license_plate || (
          <span className="text-dim">{t('analytics.vehiclePlNoPlate')}</span>
        )}
      </td>

      <td className="py-1.5 pr-3">
        <div>{row.service_type}</div>
        <div className="text-[10px] text-dim">
          {[
            row.interval_km == null ? null : t('servicing.everyKm', { n: km(row.interval_km) }),
            row.interval_months == null
              ? null
              : t('servicing.everyMonths', { n: row.interval_months }),
            // Which rule produced this row. A carrier who wants a different
            // interval on one truck needs to know whether he is looking at
            // his fleet default or at an override he already made, or he
            // will edit the default and move all twenty.
            row.plan_scope === 'vehicle' ? t('servicing.planScopeVehicle') : null,
          ].filter(Boolean).join(' · ')}
        </div>
      </td>

      <td className="py-1.5 pr-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.03em] ${
            STATUS_TONE[row.status] ?? 'bg-white/6 text-dim'
          }`}
        >
          {t(STATUS_KEYS[row.status] ?? 'servicing.status.unknown')}
        </span>
      </td>

      <td className="py-1.5 pr-3 tabular-nums">
        {dateText == null ? <span className="text-dim">{EM_DASH}</span> : <div>{dateText}</div>}
        <div className="text-[10px] text-dim">
          {row.last_performed_at
            ? t('servicing.lastService', { d: row.last_performed_at })
            : t('servicing.lastServiceNever')}
          {row.last_odometer_km == null
            ? ''
            : ` · ${t('servicing.lastServiceOdometer', { n: km(row.last_odometer_km) })}`}
        </div>
        {/* A plan with history is a deadline; a plan without one is a job
            nobody has recorded, and the fix is a form, not a workshop. */}
        {row.status === 'no_baseline' ? (
          <div className="text-[10px] leading-snug text-structure">{t('servicing.noBaselineNote')}</div>
        ) : null}
        {DATE_REASON_KEYS[row.km_reason] ? (
          <div className="text-[10px] leading-snug text-accent">
            {t(DATE_REASON_KEYS[row.km_reason])}
          </div>
        ) : null}
      </td>

      <td className="py-1.5">
        {row.km_status == null ? (
          // The plan sets no kilometre interval at all. Nothing is missing
          // here and nothing needs fixing — this is a date-only rule.
          <span className="text-dim">{EM_DASH}</span>
        ) : (
          <>
            <div className="tabular-nums">{kmDeadline(t, row)}</div>
            {row.due_km == null ? null : (
              <div className="text-[10px] tabular-nums text-dim">
                {t('servicing.dueAtKm', { n: km(row.due_km) })}
              </div>
            )}
            {/* Four reasons, four fixes, four sentences.
                Not on a no_baseline row, though: the engine reports its km
                reason as last_service_has_no_odometer, which is true in the
                sense that there is no last service to carry one — but the
                sentence points at a field on a record that does not exist,
                and the row's real call to action ("type in when this was
                last done") is already in the date column. Sending a carrier
                to edit a service record he has never created is the same
                wrong-screen failure the four separate sentences exist to
                avoid. */}
            {row.status !== 'no_baseline'
            && row.km_status === 'unknown' && KM_REASON_KEYS[row.km_reason] ? (
              <div className="text-[10px] leading-snug text-accent">
                {t(KM_REASON_KEYS[row.km_reason], { n: row.odometer_age_days ?? 0 })}
              </div>
            ) : null}
            <div className="text-[10px] text-dim">{odometerProvenance(t, row)}</div>
          </>
        )}
      </td>
    </tr>
  );
}
